import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import { Store } from '../../contexts/StoreContext';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const { selectedStore, userStores, selectStore, isLoadingStores } = useStore();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Get display name for the user
  const getUserDisplayName = (): string => {
    if (!currentUser) return 'User';
    
    if (currentUser.first_name && currentUser.last_name) {
      return `${currentUser.first_name} ${currentUser.last_name}`;
    } else if (currentUser.first_name) {
      return currentUser.first_name;
    } else {
      return currentUser.username;
    }
  };

  // Get user initial for avatar
  const getUserInitial = (): string => {
    if (!currentUser) return 'U';
    
    if (currentUser.first_name) {
      return currentUser.first_name.charAt(0).toUpperCase();
    } else {
      return currentUser.username.charAt(0).toUpperCase();
    }
  };

  const getUserAvatar = () => {
    if (currentUser?.image_url) {
      return (
        <img
          src={currentUser.image_url}
          alt="User Avatar"
          className={styles.userAvatarImage}
        />
      );
    }
    return getUserInitial();
  };

  const handleStoreChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const storeId = parseInt(event.target.value, 10);
    const storeToSelect = userStores.find(s => s.id === storeId);
    if (storeToSelect) {
      selectStore(storeToSelect);
      navigate(`/store/${storeToSelect.id}/overview`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>
            <Link to="/" className={styles.logoLink} title="Moujood CMS">
              Moujood CMS
            </Link>
          </div>
          
          <div className={styles.storeDisplay}>
            {selectedStore ? (
              <div className={styles.storeNameContainer}>
                <span className={styles.storeName} title={selectedStore.store_name}>{selectedStore.store_name}</span>
                {!selectedStore.is_active && (
                  <span className={styles.pendingBadge} title="Store request pending approval">
                    ⏳ Pending
                  </span>
                )}
              </div>
            ) : isLoadingStores ? (
              <span className={styles.loadingStore} title="Loading store...">Loading store...</span>
            ) : (
              <span className={styles.noStoreSelected} title="No Store Selected">No Store Selected</span>
            )}
          </div>
        </div>
        
        <div className={styles.headerRight}>
          {userStores.length > 1 && selectedStore && (
            <div className={styles.storeSwitcher}>
              <select 
                value={selectedStore.id} 
                onChange={handleStoreChange} 
                className={styles.storeSelectDropdown}
                aria-label="Switch store"
                title="Switch store"
              >
                {userStores.map((store: Store) => (
                  <option key={store.id} value={store.id}>
                    {store.store_name} {!store.is_active ? '(Pending)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className={styles.userActionsContainer}>
            <button 
              className={styles.userButton} 
              onClick={toggleUserMenu} 
              aria-label="User profile menu"
              title={getUserDisplayName()}
            >
              <div className={styles.userAvatar}>
                {getUserAvatar()}
              </div>
              <span className={styles.userName} title={getUserDisplayName()}>{getUserDisplayName()}</span>
              <span className={styles.dropdownArrow}></span>
            </button>
            
            {showUserMenu && (
              <div className={styles.userMenu}>
                <Link to="/profile" className={styles.userMenuItem} title="Profile">Profile</Link>
                <Link to="/select-store" className={styles.userMenuItem} title="Switch Store">Switch Store</Link>
                <button onClick={handleLogout} className={styles.userMenuItem} title="Logout">Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 